import React, { useState, useRef, useLayoutEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { fromJS } from "immutable";
import { ChevronsUpDown, PencilIcon, Search } from "lucide-react";
import { Checkbox, Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import InputDebounce from "./InputDebounce";
import useResizeObserver from "../useResizeObserver";
import Avatar from "./Avatar";

export const ChipAssignees = ({ type, assignedMembers, onlyAvatar }) => {
  const [containerAssigneeRef, size]: any = useResizeObserver();

  const visibleCount = useMemo(() => {
    if (assignedMembers.size === 1) return 1;

    const gap = 4; // gap-1
    const avatarSize6 = 24; // width-6
    const avatarSize5 = 20; // width-5
    const estimateChipWidth = (text) => {
      if (onlyAvatar) {
        if (type === "FORM") {
          return avatarSize6 - 4;
        }
        return avatarSize5 - 4;
      } else {
        if (type === "FORM") {
          return text.length * 6 + gap * 4 + avatarSize6 + 8;
        }
        return text.length * 5 + gap * 4 + avatarSize5 + 8;
      }
    };

    let width = size.width;
    if (type === "INLINE") {
      width = width - 48; // right icon
    } else if (type === "FORM") {
      width = width - 48; // right icon
    } else if (type === "GRID") {
    }
    const viewAllWidth = estimateChipWidth(`+${assignedMembers.size}`);
    let used = 0;
    let count = 0;

    const assignedLabelsData = assignedMembers.toJS();

    for (let i = 0; i < assignedLabelsData.length; i++) {
      const chipWidth = estimateChipWidth(assignedLabelsData[i].displayName || "");
      const next = used + chipWidth + gap + viewAllWidth;
      if (next > width) break;
      used += chipWidth + gap;
      count++;
    }

    return count;
  }, [type, assignedMembers, onlyAvatar, size.width]);

  const titleAssignedMembers = assignedMembers
    .toJS()
    .slice(visibleCount, assignedMembers.size)
    .map((item) => item.displayName)
    .join("\n");

  let chipClass = "px-2 py-0.5 text-[10px] font-medium leading-4";
  let sizeIcon = 6;
  let valueClassName = "text-sm font-medium leading-4";
  if (type === "INLINE") {
    sizeIcon = 5;
    valueClassName = "text-xs font-medium leading-4";
  } else if (type === "FORM") {
    chipClass = "px-2 py-1.5 text-xs font-medium leading-4";
  } else if (type === "GRID") {
  }

  return (
    <div ref={containerAssigneeRef} className="w-full flex flex-nowrap gap-1 overflow-hidden">
      {assignedMembers
        .toJS()
        .slice(0, visibleCount)
        .map((item) => {
          const { accountId, displayName, avatarUrl } = item;
          return (
            <div
              key={accountId}
              title={displayName}
              className={`shrink-0 ${
                onlyAvatar ? "-mr-3" : "mr-2"
              } flex items-center flex-nowrap gap-2 overflow-hidden cursor-default`}
            >
              <Avatar avatarUrl={avatarUrl} displayName={displayName} sizeIcon={sizeIcon} />
              {!onlyAvatar || assignedMembers.size === 1 ? (
                <div className={valueClassName}>{displayName}</div>
              ) : null}
            </div>
          );
        })}
      {visibleCount < assignedMembers.size && (
        <button
          title={titleAssignedMembers}
          className={`shrink-0 flex items-center gap-1 rounded-full ${chipClass} cursor-pointer bg-gray-100 text-gray-600`}
        >
          +{assignedMembers.size - visibleCount}
        </button>
      )}
    </div>
  );
};

interface AssigneesSelectProps {
  type: "INLINE" | "FORM" | "GRID";
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
}

const AssigneesSelect = ({ type, value, onChange, placeholder }: AssigneesSelectProps) => {
  const { t } = useTranslation();

  const popoverButtonRef = useRef(null);
  const popoverPanelRef = useRef(null);
  const [panelWidth, setPanelWidth] = useState(null);

  const [keywords, setKeywords] = useState("");

  const allMembers =
    useSelector((state: any) => state.projectReducer.getIn(["project", "members"])) || fromJS([]);

  const renderSearch = () => {
    return (
      <div className="w-full flex items-center">
        <Search className="w-4 h-4 text-gray-400" />
        <InputDebounce
          autoFocus
          className={`w-full h-6 px-2 placeholder:text-gray-400 text-sm focus:outline-none`}
          placeholder={t("Search...")}
          value={keywords}
          onChange={(value: string) => {
            setKeywords(value);
          }}
        />
      </div>
    );
  };

  const renderAssigneeList = () => {
    if (allMembers.size === 0) {
      return (
        <div className="flex items-center justify-center p-1 text-gray-400">{t("No options")}</div>
      );
    }
    const memberList = allMembers
      .toJS()
      .map((item) => ({ ...item, selected: value.includes(item.accountId) }))
      .sort((a, b) => Number(b.selected) - Number(a.selected));
    const lastSelectedIndex = memberList.map((u) => u.selected).lastIndexOf(true);
    return memberList
      .filter((item) =>
        keywords
          ? item.displayName.trim().toLowerCase().includes(keywords.trim().toLowerCase())
          : true
      )
      .map((item, index) => {
        const { accountId, displayName, email, avatarUrl } = item;
        const showDevide = lastSelectedIndex === index && index < memberList.length - 1;
        return (
          <React.Fragment key={accountId}>
            <div
              className="flex items-center group p-1 rounded-md hover:bg-gray-100 transition-colors gap-2 cursor-default"
              onClick={() => {
                if (value.includes(accountId)) {
                  const newValues = value.filter((val) => val !== accountId);
                  onChange(newValues);
                } else {
                  onChange([...value, accountId]);
                }
              }}
            >
              <div className="w-full flex items-center gap-2 cursor-default">
                <Avatar avatarUrl={avatarUrl} displayName={displayName} />
                <div className="text-sm font-medium leading-4">{displayName}</div>
                {email ? (
                  <div className="text-xs font-medium leading-4 text-gray-600">{email}</div>
                ) : null}
              </div>
              <Checkbox
                checked={value.includes(accountId)}
                className="group block size-4 rounded border border-gray-300 bg-white data-checked:bg-blue-500"
              >
                <svg
                  className="stroke-white opacity-0 group-data-checked:opacity-100"
                  viewBox="0 0 14 14"
                  fill="none"
                >
                  <path
                    d="M3 8L6 11L11 3.5"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </Checkbox>
            </div>
            {showDevide ? <div className="w-full my-1 border-b border-gray-300"></div> : null}
          </React.Fragment>
        );
      });
  };

  const renderTarget = (isOpen) => {
    const assignedMembers = allMembers.filter((item) => value.includes(item.get("accountId")));
    if (type === "INLINE") {
      return (
        <div className="group w-full flex items-center justify-between rounded-md transition-colors duration-150 hover:bg-gray-50 flex-1 min-h-[28px] cursor-pointer">
          {value.length > 0 ? (
            <ChipAssignees type={type} assignedMembers={assignedMembers} onlyAvatar={false} />
          ) : (
            <div className="w-full text-left font-medium text-xs leading-4 text-gray-400">
              {placeholder}
            </div>
          )}
          <div className="ml-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <PencilIcon className="w-3 h-3 text-gray-400 mr-2" />
          </div>
        </div>
      );
    } else if (type === "FORM") {
      return (
        <div
          className={`w-full h-9.5 flex items-center justify-between overflow-hidden gap-2 px-3 cursor-default rounded-lg ${
            isOpen ? "ring-4 ring-blue-100 border border-blue-600" : "border border-gray-200"
          }`}
        >
          {value.length > 0 ? (
            <ChipAssignees type={type} assignedMembers={assignedMembers} onlyAvatar={false} />
          ) : (
            <div className="flex items-center flex-nowrap overflow-hidden gap-2">
              <div className="text-sm leading-4 text-gray-500 truncate">{placeholder}</div>
            </div>
          )}
          <div className="shrink-0 flex items-center flex-nowrap gap-4">
            <ChevronsUpDown className="w-3.5 h-3.5 text-gray-400" />
          </div>
        </div>
      );
    } else if (type === "GRID") {
      return (
        <div className="w-full cursor-pointer">
          {value.length > 0 ? (
            <ChipAssignees type={type} assignedMembers={assignedMembers} onlyAvatar={true} />
          ) : (
            <div className="w-full text-left leading-4 text-gray-500 cursor-default">
              {placeholder}
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  let popoverPanelClassName = "";
  if (type === "INLINE") {
  } else if (type === "FORM") {
    popoverPanelClassName = "mt-2";
  } else if (type === "GRID") {
    popoverPanelClassName = "mt-1";
  }

  return (
    <Popover className="w-full">
      {({ open }) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useLayoutEffect(() => {
          if (!open) return;
          if (!popoverButtonRef.current || !popoverPanelRef.current) return;
          const buttonWidth = popoverButtonRef.current.offsetWidth;
          const panelNaturalWidth = popoverPanelRef.current.offsetWidth;
          setPanelWidth(Math.max(buttonWidth, panelNaturalWidth));
        }, [open]);

        return (
          <>
            <PopoverButton
              ref={popoverButtonRef}
              as="div"
              onClick={(e) => {
                if (open) {
                  e.preventDefault();
                }
              }}
            >
              {renderTarget(open)}
            </PopoverButton>
            <PopoverPanel
              ref={popoverPanelRef}
              anchor="bottom start"
              style={{ width: panelWidth ?? "auto" }}
              className={`z-9999 p-2 bg-white rounded-md border border-gray-300 shadow-xl ${popoverPanelClassName}`}
            >
              <div>
                {renderSearch()}
                <div className="w-full my-1 border-b border-gray-200"></div>
                <div className="w-full max-h-60 overflow-y-auto">{renderAssigneeList()}</div>
              </div>
            </PopoverPanel>
          </>
        );
      }}
    </Popover>
  );
};

export default AssigneesSelect;
